/**
 * Okutulan ürün listesindeki kodları panoya taşır.
 *
 * Çıktı bilinçli olarak "satır başına bir kod" düz metnidir. OPAS'ın yapıştırma
 * kabul eden alanları (satış, karekod doğrulama, fatura, takas) yapıştırılan metni
 * satırlara bölerek okuduğu için aynı metin hem uygulamanın içine hem not defterine
 * yapıştırılabilir; ayrı bir "OPAS biçimi" uydurmak listeyi dışarıya taşınamaz kılardı.
 *
 * Karekodu olmayan (yalnızca barkodla okutulan) ürün atlanmaz, barkodu yazılır:
 * atlanırsa liste yapıştırıldığında o ürün sessizce kaybolur ve kullanıcı eksiği
 * ancak sayım sırasında fark eder.
 */

import { gs1SktYyMmDd, normalizeGs1Raw, parseDataMatrix, buildDataMatrixFromIts } from '@/utils/dataMatrixParser'

export type PanoyaKopyalanacakSatir = {
  /** Ham GS1 okuma. Aynı ürünün birden çok kutusu satır satır (\n) tutulabilir. */
  dataMatrixRaw?: string | null
  /** Karekodsuz okutmalarda kullanılacak barkod; GTIN de olabilir. */
  barkod?: string | null
  /** Satırdaki kutu sayısı. Karekodu eksik kalan kutular barkodla tamamlanır. */
  adet?: number | null
}

export type KopyalamaOzeti = {
  metin: string
  karekodSayisi: number
  barkodSayisi: number
  /** Panoya yazılan toplam satır sayısı. */
  toplam: number
}

/** Yalnızca GS1 karekodu: barkodla karışmaması için AI 01 ile başlamalı. */
function karekodMu(kod: string): boolean {
  return kod.startsWith('01') && kod.length >= 15
}

function barkoduTemizle(deger: string | null | undefined): string | null {
  const temiz = (deger ?? '').replace(/\D/g, '')
  // Yapıştırma tarafı 8–14 hanelik sayıyı barkod sayar; dışında kalan değer
  // yapıştırıldığında metin aramasına düşer ve ürün eklenmez.
  return temiz.length >= 8 && temiz.length <= 14 ? temiz : null
}

function satirinKarekodlari(satir: PanoyaKopyalanacakSatir): string[] {
  if (!satir.dataMatrixRaw) return []
  return satir.dataMatrixRaw
    .split(/\r\n|\n|\r/)
    .map(parca => normalizeGs1Raw(parca))
    .filter(karekodMu)
}

/**
 * Panoya yazılacak metni üretir. Aynı karekod iki kez yazılmaz: karekod tek bir
 * kutuyu gösterir, yinelenen satır yapıştırıldığında "bu kutu zaten okutuldu"
 * uyarısı üretip kullanıcıyı yok yere meşgul eder. Barkodlar ise kutu sayısını
 * taşıdığı için tekrar edebilir.
 */
export function kopyalamaMetniOlustur(satirlar: readonly PanoyaKopyalanacakSatir[]): KopyalamaOzeti {
  const cikti: string[] = []
  const gorulenKarekodlar = new Set<string>()
  let karekodSayisi = 0
  let barkodSayisi = 0

  for (const satir of satirlar) {
    const karekodlar = satirinKarekodlari(satir)
    for (const karekod of karekodlar) {
      if (gorulenKarekodlar.has(karekod)) continue
      gorulenKarekodlar.add(karekod)
      cikti.push(karekod)
      karekodSayisi += 1
    }

    // Karekodsuz kalan kutular: hiç okutulmamış olabilir ya da kutunun bir kısmı
    // karekodla, kalanı elle eklenmiş olabilir. İki durumda da adet korunmalı.
    const adet = Math.max(0, Math.trunc(satir.adet ?? 1))
    const karekodsuzKutu = Math.max(0, (adet || 1) - karekodlar.length)
    if (karekodsuzKutu === 0) continue

    const barkod = barkoduTemizle(satir.barkod)
    if (!barkod) continue

    for (let i = 0; i < karekodsuzKutu; i += 1) {
      cikti.push(barkod)
      barkodSayisi += 1
    }
  }

  return {
    metin: cikti.join('\n'),
    karekodSayisi,
    barkodSayisi,
    toplam: cikti.length,
  }
}

/**
 * Panoya yazar. `navigator.clipboard` yalnızca güvenli bağlamda ve kullanıcı
 * etkileşimiyle çalışır; eski tarayıcıda veya http üzerinden çalışan kurulumda
 * sessizce kaybolmasın diye geçici bir alan üzerinden yedek yol denenir.
 */
export async function panoyaYaz(metin: string): Promise<boolean> {
  if (!metin) return false

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(metin)
      return true
    }
  } catch {
    // Yedek yola düşülür.
  }

  try {
    const alan = document.createElement('textarea')
    alan.value = metin
    alan.setAttribute('readonly', '')
    alan.style.position = 'fixed'
    alan.style.opacity = '0'
    alan.style.pointerEvents = 'none'
    document.body.appendChild(alan)
    alan.select()
    const yazildi = document.execCommand('copy')
    document.body.removeChild(alan)
    return yazildi
  } catch {
    return false
  }
}

/** Listeyi panoya kopyalar; başarısızsa özet döner ama `toplam` 0'dır. */
export async function karekodlariPanoyaKopyala(
  satirlar: readonly PanoyaKopyalanacakSatir[],
): Promise<KopyalamaOzeti> {
  const ozet = kopyalamaMetniOlustur(satirlar)
  if (!ozet.toplam) return ozet
  const yazildi = await panoyaYaz(ozet.metin)
  return yazildi ? ozet : { metin: '', karekodSayisi: 0, barkodSayisi: 0, toplam: 0 }
}

/** Kutu başına saklanan karekod izi. Fatura ve takas satırları karekodu bu alanlarda tutar. */
export type KutuIzi = {
  seri?: string | null
  parti?: string | null
  /** YYYY-MM-DD, saatli ISO, YYMMDD veya gün.ay.yıl. */
  skt?: string | Date | null
  /** Okuyucudan gelen ham GS1; eksikse (SKT/parti yok) alanlardan tamamlanır. */
  ham?: string | null
}

/** GS1 / İTS için barkodu 14 haneye çevirir (13 → başına 0). */
export function gtin14(deger: string | null | undefined): string | null {
  const rakamlar = (deger ?? '').replace(/\D/g, '')
  if (rakamlar.length === 14) return rakamlar
  if (rakamlar.length === 13) return `0${rakamlar}`
  return null
}

/**
 * Kutu izinden okunabilir bir karekod kurar: 01 GTIN, 21 seri, 17 SKT, 10 parti.
 *
 * Saklı ham metin dört alanı da taşıyorsa korunur (ayırıcı karakterler kalır).
 * SKT veya parti ham metinde yoksa, kutunun kendi alanlarından yeniden kurulur;
 * aksi halde ekranda ve panoda karekod yarım kalır.
 */
export function kutudanKarekodKur(barkod: string | null | undefined, kutu: KutuIzi): string | null {
  const seri = (kutu.seri ?? '').trim()
  const parti = (kutu.parti ?? '').trim()
  const kurulan = buildDataMatrixFromIts({
    gtin: barkod ?? undefined,
    sn: seri || undefined,
    bn: parti || undefined,
    xd: kutu.skt,
  })
  const kurulanTam = kurulan && kurulan.startsWith('01') && seri ? kurulan : null

  const sakliHam = normalizeGs1Raw(kutu.ham ?? '')
  if (sakliHam && sakliHam.startsWith('01') && sakliHam.length >= 15) {
    const parsed = parseDataMatrix(sakliHam)
    const sakliGtin = gtin14(parsed.gtin)
    const beklenenGtin = gtin14(barkod)
    const seriUyumlu =
      Boolean(parsed.serialNumber) &&
      (!seri || parsed.serialNumber!.trim() === seri)
    const gtinUyumlu = !beklenenGtin || !sakliGtin || sakliGtin === beklenenGtin
    const sktEksik = Boolean(gs1SktYyMmDd(kutu.skt)) && !parsed.expiryDate
    const partiEksik = Boolean(parti) && !parsed.lotNumber
    if (seriUyumlu && gtinUyumlu && !sktEksik && !partiEksik && parsed.serialNumber) {
      return sakliHam
    }
  }

  return kurulanTam
}

/**
 * Karekodu ham hâliyle saklamayan ekranlar (fatura, takas) için satır üretir:
 * kutu izlerinden karekod kurulur, kurulamayan kutular barkoda düşer.
 */
export function kutulardanSatir(args: {
  barkod?: string | null
  kutular?: readonly KutuIzi[] | null
  adet?: number | null
}): PanoyaKopyalanacakSatir {
  const kodlar = (args.kutular ?? [])
    .map(kutu => kutudanKarekodKur(args.barkod, kutu))
    .filter((kod): kod is string => kod !== null)

  return {
    dataMatrixRaw: kodlar.length ? kodlar.join('\n') : undefined,
    barkod: args.barkod,
    adet: args.adet ?? kodlar.length ?? 1,
  }
}

/** Menüde gösterilecek özet: "12 karekod, 2 barkod". */
export function kopyalamaOzetMetni(ozet: KopyalamaOzeti): string {
  const parcalar: string[] = []
  if (ozet.karekodSayisi) parcalar.push(`${ozet.karekodSayisi} karekod`)
  if (ozet.barkodSayisi) parcalar.push(`${ozet.barkodSayisi} barkod`)
  return parcalar.join(', ')
}
